import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Modal, Button, Spinner, Alert } from 'react-bootstrap';
import { Landmark, Search, ShieldCheck, Clock, ChevronRight, AlertTriangle } from 'lucide-react';

/**
 * BankConnectModal — Plaid-Link/midday-style institution picker
 * (BANK_CONNECTION_PLAN.md §3.6, UX round 2).
 *
 * Flow: open → search-first list (popular Swedish banks pinned, logos when
 * the provider supplies them) → pick a bank → consent explainer (what we
 * read, 180-day BankID renewal, per-bank quirks) → "Fortsätt till banken"
 * (full-page redirect handled by the parent's onConnect).
 *
 * The list is provider-agnostic: it renders whatever the backend's /banks
 * endpoint returns (Enable Banking today).
 */

const POPULAR_BANKS = [
    'Swedbank',
    'Handelsbanken',
    'SEB',
    'Nordea',
    'Länsförsäkringar Bank',
    'Danske Bank',
];

const BANK_HINTS = {
    Handelsbanken:
        'Företagskonton kräver att en fullmakt för öppna banktjänster är registrerad i Handelsbankens internetbank.',
    Nordea:
        'Nordeas företagskonton ligger under egna val i listan (t.ex. "Nordea Corporate") — välj den variant där företagets konton finns.',
};

export function BankLogo({ bank }) {
    const [failed, setFailed] = useState(false);
    if (bank.logoUrl && !failed) {
        return (
            <img
                src={bank.logoUrl}
                alt=""
                width={36}
                height={36}
                style={{ borderRadius: 8, objectFit: 'contain', background: '#fff', border: '1px solid #f3f4f6' }}
                onError={() => setFailed(true)}
            />
        );
    }
    return (
        <span
            style={{
                width: 36,
                height: 36,
                borderRadius: 8,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'var(--rgba-primary-1)',
                color: 'var(--primary)',
                flexShrink: 0,
            }}
        >
            <Landmark size={18} />
        </span>
    );
}

const BankConnectModal = ({ show, onHide, banks, banksLoading, onConnect, connecting }) => {
    const [query, setQuery] = useState('');
    const [selected, setSelected] = useState(null); // bank object → consent step
    const searchRef = useRef(null);

    useEffect(() => {
        if (show) {
            setQuery('');
            setSelected(null);
            // Search-first: focus as soon as the modal paints.
            setTimeout(() => searchRef.current?.focus(), 120);
        }
    }, [show]);

    const { popular, rest } = useMemo(() => {
        const q = query.trim().toLowerCase();
        const filtered = q ? banks.filter((b) => b.name.toLowerCase().includes(q)) : banks;
        if (q) return { popular: [], rest: filtered };
        const popularSet = [];
        for (const name of POPULAR_BANKS) {
            const hit = filtered.find((b) => b.name === name || b.name.startsWith(`${name} `));
            if (hit) popularSet.push(hit);
        }
        const popularNames = new Set(popularSet.map((b) => b.name));
        return {
            popular: popularSet,
            rest: filtered.filter((b) => !popularNames.has(b.name)),
        };
    }, [banks, query]);

    const consentDays = selected?.maxConsentSeconds
        ? Math.round(selected.maxConsentSeconds / 86400)
        : 180;

    const bankRow = (bank) => (
        <button
            key={bank.name}
            type="button"
            onClick={() => (BANK_HINTS[bank.name] ? setSelected(bank) : onConnect(bank.name))}
            disabled={connecting}
            style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                width: '100%',
                padding: '10px 12px',
                border: 'none',
                borderRadius: 10,
                background: 'transparent',
                textAlign: 'left',
                cursor: 'pointer',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = '#f9fafb')}
            onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
        >
            <BankLogo bank={bank} />
            <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: 'block', fontWeight: 500, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {bank.name}
                </span>
                <span style={{ fontSize: 12, color: '#9ca3af' }}>via Enable Banking</span>
            </span>
            <ChevronRight size={16} style={{ color: '#9ca3af', flexShrink: 0 }} />
        </button>
    );

    return (
        <Modal show={show} onHide={connecting ? undefined : onHide} centered>
            <Modal.Header closeButton={!connecting}>
                <Modal.Title style={{ fontSize: 17, display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span
                        style={{
                            width: 36,
                            height: 36,
                            borderRadius: 10,
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: 'var(--rgba-primary-1)',
                            color: 'var(--primary)',
                        }}
                    >
                        <Landmark size={18} />
                    </span>
                    {selected ? `Anslut ${selected.name}` : 'Välj din bank'}
                </Modal.Title>
            </Modal.Header>

            <Modal.Body style={{ padding: selected ? '20px 24px' : '12px 12px 4px' }}>
                {!selected ? (
                    <>
                        <div style={{ position: 'relative', margin: '4px 8px 10px' }}>
                            <Search size={16} style={{ position: 'absolute', left: 12, top: 11, color: '#9ca3af' }} />
                            <input
                                ref={searchRef}
                                type="text"
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                placeholder="Sök bank…"
                                style={{
                                    width: '100%',
                                    padding: '9px 12px 9px 36px',
                                    borderRadius: 10,
                                    border: '1px solid #e5e7eb',
                                    fontSize: 14,
                                    outline: 'none',
                                }}
                            />
                        </div>

                        {/* Fixed height: the modal must not resize as search narrows the list. */}
                        <div style={{ height: 380, overflowY: 'auto', padding: '0 4px 8px' }}>
                            {banksLoading ? (
                                <div className="text-center py-5">
                                    <Spinner animation="border" size="sm" />
                                    <div style={{ fontSize: 13, color: '#6b7280', marginTop: 8 }}>Hämtar banker…</div>
                                </div>
                            ) : banks.length === 0 ? (
                                <Alert variant="warning" className="mx-2" style={{ fontSize: 13 }}>
                                    <AlertTriangle size={14} className="me-2" />
                                    Banklistan kunde inte hämtas just nu. Försök igen om en stund.
                                </Alert>
                            ) : (
                                <>
                                    {popular.length > 0 && (
                                        <>
                                            <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: 0.5, textTransform: 'uppercase', color: '#9ca3af', padding: '6px 12px 2px' }}>
                                                Vanliga banker
                                            </div>
                                            {popular.map(bankRow)}
                                            {rest.length > 0 && (
                                                <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: 0.5, textTransform: 'uppercase', color: '#9ca3af', padding: '10px 12px 2px' }}>
                                                    Alla banker
                                                </div>
                                            )}
                                        </>
                                    )}
                                    {rest.map(bankRow)}
                                    {popular.length === 0 && rest.length === 0 && (
                                        <div style={{ textAlign: 'center', color: '#6b7280', fontSize: 13, padding: '24px 0' }}>
                                            Ingen bank matchar "{query}". Saknas din bank? Du kan alltid importera
                                            kontoutdrag som CSV under Bankhändelser.
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8,
                                padding: '10px 14px 8px',
                                borderTop: '1px solid #f3f4f6',
                                fontSize: 12,
                                color: '#9ca3af',
                            }}
                        >
                            <ShieldCheck size={14} style={{ flexShrink: 0 }} />
                            Läsbehörighet till kontoutdrag och saldon — aldrig betalningar. Godkänns med BankID.
                        </div>
                    </>
                ) : (
                    <>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                            <BankLogo bank={selected} />
                            <div>
                                <div style={{ fontWeight: 600, color: '#0f172a' }}>{selected.name}</div>
                                <div style={{ fontSize: 12, color: '#6b7280' }}>Företagskonton · BankID</div>
                            </div>
                        </div>

                        <div style={{ display: 'grid', gap: 10, fontSize: 13, color: '#374151', marginBottom: 12 }}>
                            <div style={{ display: 'flex', gap: 10 }}>
                                <ShieldCheck size={16} style={{ color: 'var(--primary)', flexShrink: 0, marginTop: 1 }} />
                                <span>
                                    Vi får <strong>läsbehörighet</strong> till kontoutdrag och saldon — aldrig möjlighet
                                    att flytta pengar. Du godkänner hos banken med BankID.
                                </span>
                            </div>
                            <div style={{ display: 'flex', gap: 10 }}>
                                <Clock size={16} style={{ color: 'var(--primary)', flexShrink: 0, marginTop: 1 }} />
                                <span>
                                    Medgivandet gäller i ca <strong>{consentDays} dagar</strong>. Vi påminner dig i god
                                    tid innan det behöver förnyas.
                                </span>
                            </div>
                        </div>

                        {BANK_HINTS[selected.name] && (
                            <Alert variant="light" style={{ fontSize: 12, color: '#6b7280', border: '1px solid #e5e7eb' }}>
                                {BANK_HINTS[selected.name]}
                            </Alert>
                        )}
                    </>
                )}
            </Modal.Body>

            {selected && (
                <Modal.Footer style={{ borderTop: '1px solid #f3f4f6' }}>
                    <Button variant="outline-secondary" size="sm" onClick={() => setSelected(null)} disabled={connecting}>
                        Byt bank
                    </Button>
                    <Button variant="primary" size="sm" onClick={() => onConnect(selected.name)} disabled={connecting}>
                        {connecting ? <Spinner animation="border" size="sm" /> : 'Fortsätt till banken'}
                    </Button>
                </Modal.Footer>
            )}
        </Modal>
    );
};

export default BankConnectModal;
